/* ==========================================================================
   COMPLEMENT CARD MANAGER
   Busca e injeta o produto complementar (LOOK_COMPLEMENTAR_OMS) em todos
   os cards da página via GraphQL batched + Section Rendering API paralelo.

   Fluxo:
     1. Observa cada [data-look-code] com IntersectionObserver (rootMargin 200px)
     2. Ao entrar na viewport, acumula o card num buffer com debounce de 100ms
     3. Ao flush: deduplica códigos, monta 1 query GraphQL com aliases
     4. Para cada handle encontrado, faz Section Rendering em paralelo
     5. Injeta [data-complement-info] e [data-complement-sizes] nos slots do card
        (substituindo os skeletons); remove slots sem resultado imediatamente
   ========================================================================== */

(function () {
  'use strict';

  var SECTION_NAME = 'product-card-complement';
  var DEBOUNCE_MS  = 100;

  /* --------------------------------------------------------------------------
     Injeta a CSS do skeleton-pulse globalmente (uma única vez)
     (pdp-manager.css só é carregado na PDP; precisamos do keyframe em qualquer página)
  -------------------------------------------------------------------------- */

  (function injectSkeletonCSS() {
    if (document.getElementById('complement-skeleton-css')) return;
    var style = document.createElement('style');
    style.id = 'complement-skeleton-css';
    style.textContent = [
      '@keyframes skeleton-pulse{0%,100%{opacity:1}50%{opacity:.3}}',
      '.skeleton-pulse{animation:skeleton-pulse 1.2s ease-in-out infinite}'
    ].join('');
    document.head.appendChild(style);
  })();

  /* --------------------------------------------------------------------------
     ComplementaryCardManager
  -------------------------------------------------------------------------- */

  function ComplementaryCardManager() {
    var storefront = window.__STOREFRONT__;
    if (!storefront || !storefront.token || !storefront.url) return;

    this._token     = storefront.token;
    this._url       = storefront.url;
    this._processed = new Set(); // cards já enfileirados (evita duplicatas)
    this._pending   = [];        // buffer acumulado durante o debounce
    this._timer     = null;

    var cards = Array.from(document.querySelectorAll('[data-look-code]'));
    if (cards.length === 0) return;

    var self = this;
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var card = entry.target;
        observer.unobserve(card);
        if (self._processed.has(card)) return;
        self._processed.add(card);
        self._pending.push(card);
        self._scheduleFlush();
      });
    }, { rootMargin: '200px' });

    cards.forEach(function (card) { observer.observe(card); });

    // Observa o grid da coleção para cards adicionados dinamicamente (load more / infinite scroll)
    var grid = document.querySelector('[data-collection-grid]');
    if (grid) {
      var mutationObserver = new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          mutation.addedNodes.forEach(function (node) {
            if (!(node instanceof Element)) return;
            var newCards = [];
            if (node.hasAttribute('data-look-code')) newCards.push(node);
            node.querySelectorAll('[data-look-code]').forEach(function (c) { newCards.push(c); });
            newCards.forEach(function (card) {
              if (self._processed.has(card)) return;
              observer.observe(card);
            });
          });
        });
      });
      mutationObserver.observe(grid, { childList: true });
    }
  }

  /* --------------------------------------------------------------------------
     Agenda o flush com debounce para agrupar cards que entram juntos
  -------------------------------------------------------------------------- */

  ComplementaryCardManager.prototype._scheduleFlush = function () {
    if (this._timer) clearTimeout(this._timer);
    var self = this;
    this._timer = setTimeout(function () {
      self._timer = null;
      var batch = self._pending.splice(0);
      if (batch.length > 0) self._flush(batch);
    }, DEBOUNCE_MS);
  };

  /* --------------------------------------------------------------------------
     Flush: agrupa por código, dispara a busca batch
  -------------------------------------------------------------------------- */

  ComplementaryCardManager.prototype._flush = function (cards) {
    var byCode = {};
    cards.forEach(function (card) {
      var code      = card.getAttribute('data-look-code');
      var productId = parseInt(card.getAttribute('data-product-id'), 10);
      if (!code) return;
      if (!byCode[code]) byCode[code] = [];
      byCode[code].push({ card: card, productId: productId });
    });

    var codes = Object.keys(byCode);
    if (codes.length === 0) return;

    this._searchBatch(codes, byCode);
  };

  /* --------------------------------------------------------------------------
     Busca batch: 1 request GraphQL com alias por código único
  -------------------------------------------------------------------------- */

  ComplementaryCardManager.prototype._searchBatch = function (codes, byCode) {
    var fields = codes.map(function (code) {
      var alias = 'code_' + code.replace(/\W/g, '_');
      return (
        alias + ': search(query: ' + JSON.stringify(code) + ', types: PRODUCT, first: 5) {' +
        '  edges { node { ... on Product { id handle } } }' +
        '}'
      );
    });

    var query = 'query BatchComplement { ' + fields.join(' ') + ' }';

    var self = this;
    fetch(this._url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this._token
      },
      body: JSON.stringify({ query: query })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) { self._processResults(codes, byCode, data); })
      .catch(function (err) {
        console.error('[ComplementManager] Erro na busca batch:', err);
        // Remove skeletons de todos os cards do batch em caso de erro
        codes.forEach(function (code) {
          (byCode[code] || []).forEach(function (g) { self._removeSlots(g.card); });
        });
      });
  };

  /* --------------------------------------------------------------------------
     Processa resultados do GraphQL e dispara Section Rendering em paralelo
  -------------------------------------------------------------------------- */

  ComplementaryCardManager.prototype._processResults = function (codes, byCode, data) {
    if (!data || !data.data) return;

    var self    = this;
    var handles = {}; // código → handle do complementar encontrado

    codes.forEach(function (code) {
      var alias = 'code_' + code.replace(/\W/g, '_');
      var edges = (data.data[alias] && data.data[alias].edges) || [];
      var nodes = edges.map(function (e) { return e.node; }).filter(function (n) { return n && n.id; });

      var group      = byCode[code] || [];
      var productIds = group.map(function (g) { return g.productId; });

      var candidate = null;
      for (var i = 0; i < nodes.length; i++) {
        var numId = parseInt(nodes[i].id.split('/').pop(), 10);
        if (productIds.indexOf(numId) === -1) {
          candidate = nodes[i];
          break;
        }
      }

      if (candidate) handles[code] = candidate.handle;
    });

    // Códigos sem complementar → remove slots imediatamente
    codes.forEach(function (code) {
      if (!handles[code]) {
        (byCode[code] || []).forEach(function (g) { self._removeSlots(g.card); });
      }
    });

    // Agrupa por handle único para evitar Section Rendering duplicado
    var byHandle = {};
    Object.keys(handles).forEach(function (code) {
      var handle = handles[code];
      if (!byHandle[handle]) byHandle[handle] = [];
      byHandle[handle].push(code);
    });

    // Section Rendering em paralelo para cada handle único
    var promises = Object.keys(byHandle).map(function (handle) {
      return self._fetchSection(handle).then(function (html) {
        if (!html) {
          // Falha no fetch — remove skeletons
          byHandle[handle].forEach(function (code) {
            (byCode[code] || []).forEach(function (g) { self._removeSlots(g.card); });
          });
          return;
        }
        byHandle[handle].forEach(function (code) {
          (byCode[code] || []).forEach(function (g) { self._inject(g.card, html); });
        });
      });
    });

    Promise.all(promises);
  };

  /* --------------------------------------------------------------------------
     Section Rendering: busca o HTML do complementar
  -------------------------------------------------------------------------- */

  ComplementaryCardManager.prototype._fetchSection = function (handle) {
    var url = '/products/' + handle + '?sections=' + SECTION_NAME;
    return fetch(url)
      .then(function (r) { return r.json(); })
      .then(function (sections) { return sections[SECTION_NAME] || null; })
      .catch(function (err) {
        console.error('[ComplementManager] Erro Section Rendering (' + handle + '):', err);
        return null;
      });
  };

  /* --------------------------------------------------------------------------
     Injeção: extrai os blocos do HTML e substitui os slots (skeletons) do card
  -------------------------------------------------------------------------- */

  ComplementaryCardManager.prototype._inject = function (card, html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');

    var infoEl  = doc.querySelector('[data-complement-info]');
    var sizesEl = doc.querySelector('[data-complement-sizes]');

    var infoSlot  = card.querySelector('[data-complement-info-slot]');
    var sizesSlot = card.querySelector('[data-complement-sizes-slot]');

    if (infoSlot && infoEl)   infoSlot.replaceWith(infoEl);
    else if (infoSlot)        infoSlot.remove();

    if (sizesSlot && sizesEl) sizesSlot.replaceWith(sizesEl);
    else if (sizesSlot)       sizesSlot.remove();
  };

  /* --------------------------------------------------------------------------
     Remove slots (e skeletons dentro deles) de um card sem complementar
  -------------------------------------------------------------------------- */

  ComplementaryCardManager.prototype._removeSlots = function (card) {
    card.querySelectorAll('[data-complement-info-slot], [data-complement-sizes-slot]').forEach(function (el) {
      el.remove();
    });
  };

  /* --------------------------------------------------------------------------
     Init
  -------------------------------------------------------------------------- */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { new ComplementaryCardManager(); });
  } else {
    new ComplementaryCardManager();
  }

})();